import {computed, effect, inject, Injectable} from '@angular/core';
import {MailboxConfiguratorStore} from './mailbox-configurator.store';
import {MailboxConfiguratorService} from './services/mailbox-configurator.service';
import {UserProfileStore} from '../../core/auth/stores/user-profile.store';
import {MailboxDTO, MailboxProvider} from './mailbox.model';
import {mapMailboxToStore} from './mailbox-configurator.mapper';
import {switchMap, throwError} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class MailboxConfiguratorFacade {

	private store = inject(MailboxConfiguratorStore);
	private service = inject(MailboxConfiguratorService);
	private profileStore = inject(UserProfileStore)

	private lastLoadedMailboxId: string | null = null;

	readonly mailboxId = computed(() =>
		this.profileStore.currentUser()?.chosen_mailbox_id ?? null
	);

	readonly step = this.store.step;
	readonly provider = this.store.provider;
	readonly inbound = this.store.inbound;
	readonly outbound = this.store.outbound;
	readonly isCustom = this.store.isCustom;
	readonly mailboxes = this.store.mailboxes;

	constructor() {
		effect(() => {
			const mailboxId = this.profileStore.currentUser()?.chosen_mailbox_id;

			if (!mailboxId) return;

			this.loadConfigurations(mailboxId);
		});

		effect(() => {
			const mailbox = this.store.loadedMailbox();
			const currentView = this.store.view();

			if (currentView === 'CREATE_MAILBOX' || currentView === 'CONFIG_WIZARD') {
				return;
			}

			if (!mailbox) {
				this.store.setView('EMPTY');
				return;
			}

			const hasConfig =
				mailbox.inbound_configuration || mailbox.outbound_configuration;

			if (!hasConfig) {
				this.store.setView('EDIT_MAILBOX');
				return;
			}

			this.store.setView('CONFIG_TABS');
		});
	}

	selectMailbox(mailboxId: string) {
		this.store.setLoadedMailbox(null);
		this.loadConfigurations(mailboxId);

		this.store.setMode('EDIT')
		this.store.setView('CONFIG_TABS')
	}

	selectProvider(provider: MailboxProvider) {
		this.store.setProvider(provider);
		this.store.setStep('INBOUND');
	}

	next() {
		const step = this.store.step();

		if (step === 'INBOUND') this.store.setStep('OUTBOUND');
		else if (step === 'OUTBOUND') this.store.setStep('REVIEW');
	}

	back() {
		const step = this.store.step();

		if (step === 'OUTBOUND') this.store.setStep('INBOUND');
		else if (step === 'REVIEW') this.store.setStep('OUTBOUND');
	}

	updateInbound(data: any) {
		this.store.patchInbound(data);
	}

	updateOutbound(data: any) {
		this.store.patchOutbound(data);
	}

	updateMailbox(mailboxId: string, body: MailboxDTO) {
		this.service.updateMailbox(body, mailboxId).subscribe({
			next: () => {
				this.loadMailboxes();
			},
			error: () => {
				this.store.setError('Errore aggiornamento mailbox');
			}
		});
	}

	startCreateMailbox() {
		this.store.setMode('CREATE');
		this.store.setView('CREATE_MAILBOX');
		this.store.reset();
	}

	startConfigWizard() {
		this.store.setMode('CREATE');
		this.store.setStep('INBOUND');
		this.store.setView('CONFIG_WIZARD');
	}

	loadMailboxes() {
		this.store.setLoadingMailboxes(true);
		this.store.setError(null);

		this.service.getMailboxes().subscribe({
			next: mailboxes => {
				this.store.setMailboxes(mailboxes);
				this.store.setLoadingMailboxes(false);
			},
			error: () => {
				this.store.setError('Errore nel caricamento mailbox');
				this.store.setLoadingMailboxes(false);
			}
		});
	}

	loadConfigurations(mailboxId: string) {
		if (this.lastLoadedMailboxId === mailboxId) return;
		this.lastLoadedMailboxId = mailboxId;

		this.store.setLoading(true);
		this.store.setError(null);

		this.service.getConfigurationsById(mailboxId).subscribe({
			next: mailbox => {
				this.store.setLoadedMailbox(mailbox);

				const mapped = mapMailboxToStore(mailbox);
				this.store.patchInbound(mapped.inbound);
				this.store.patchOutbound(mapped.outbound);

				this.store.setLoading(false);
			},
			error: () => {
				this.store.setError('Errore nel caricamento mailbox');
				this.store.setLoading(false);
			}
		});
	}

	reset() {
		this.store.reset();
	}

	saveMailbox(body: MailboxDTO) {
		this.service.addMailbox(body).subscribe({
			next: () => {
				this.loadMailboxes()
				this.store.setView('EMPTY')

			},
			error: () => {
				this.store.setError('Errore creazione mailbox');
			}
		});
	}

	saveConfigurations(
		inboundPayload: Record<string, unknown>,
		outboundPayload: Record<string, unknown>,
	) {
		const mailboxId = this.store.loadedMailbox()?.id;

		if (!mailboxId) {
			return throwError(() => new Error('Mailbox non selezionata'));
		}

		return this.service.createInbound(inboundPayload).pipe(
			switchMap((inboundRes: any) => {
				const inboundId = inboundRes.id;

				return this.service.updateMailboxInbound(mailboxId, inboundId).pipe(
					switchMap(() =>
						this.service.createOutbound(outboundPayload)
					),
					switchMap((outboundRes: any) => {
						const outboundId = outboundRes.id;

						return this.service.updateMailboxOutbound(mailboxId, outboundId);
					}),
					switchMap(() =>
						this.service.startListener({
							mailboxId,
							inboundConfigurationId: inboundId,
						})
					)
				);
			})
		);
	}
}
